import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { addDays, format, startOfWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import * as moment from 'moment';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { firstValueFrom } from 'rxjs';
import { AlertService } from 'src/app/_services/alert.service';
import { RequestService } from 'src/app/_services/request.service';
import { UtilityService } from 'src/app/_services/utility.service';

function weekChangedValidator(initialValues: {
  [key: string]: number;
}): ValidatorFn {
  /**
   * Validator custom usato dal tab smart per capire se la settimana è stata
   * davvero modificata rispetto allo stato iniziale caricato dal backend.
   */
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const isSame = Object.keys(initialValues).every(
      key => control.value[key] === initialValues[key]
    );

    return isSame ? { weekNotChanged: true } : null;
  };
}

@Component({
  selector: 'app-WorkplaceRequest',
  templateUrl: './WorkplaceRequest.component.html',
  styleUrls: [
    './WorkplaceRequest.component.css',
    '../../review-modal/review-modal.component.css'
  ]
})
export class WorkplaceRequestComponent implements OnInit {
  @Output() saved = new EventEmitter<any>();

  private readonly alertService: AlertService = inject(AlertService);
  public formBuilder: FormBuilder = inject(FormBuilder);
  public readonly utilityService: UtilityService = inject(UtilityService);
  public rs: RequestService = inject(RequestService);

  // input arrivati dal componente Calendar
  readonly modalData: any = inject(NZ_MODAL_DATA);

  request?: any;
  user: any;
  date: string;
  edit: boolean;
  activeTab: 'smart' | 'schedule' = 'smart';
  isPartTimeUser = false;

  // form
  workPlaceRequestForm: FormGroup;
  scheduleRequestForm: FormGroup;
  // variabili per la gestione del calendario settimanale
  currentWeekStart: Date = startOfWeek(new Date(), { weekStartsOn: 1 }); // lunedì
  weekDays: { date: Date; label: string }[] = [];
  selected: string[] = Array(5).fill(0);
  maxSmartDays: number = 0;
  userWeek: any[] = [];
  copyUserWeek: any[] = [];
  totalSmartDays: number = 0;

  // Variabile per disabilitare la selezione settimana dopo la modifica
  weekNavigationDisabled = false;
  initialWeekValues!: { [key: string]: number };
  isLoadingWeek = true;
  isLoadingScheduleWeek = false;
  partTimeWeeklyMinutes = 0;
  initialScheduleValues = '';
  hasPendingScheduleRequest = false;
  scheduleWeekSource: 'contract' | 'request' | 'pending_request' | '' = '';
  readonly weeklyDays = [
    { weekday: 1, label: 'LUNEDI' },
    { weekday: 2, label: 'MARTEDI' },
    { weekday: 3, label: 'MERCOLEDI' },
    { weekday: 4, label: 'GIOVEDI' },
    { weekday: 5, label: 'VENERDI' }
  ];
  readonly disabledScheduleHours = (): number[] => [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 19, 20, 21, 22, 23
  ];
  readonly disabledScheduleMinutes = (selectedHour: number): number[] =>
    selectedHour === 18
      ? Array.from({ length: 59 }, (_, index) => index + 1)
      : [];

  constructor(private readonly modalRef: NzModalRef) {}

  /**
   * Inizializza la modale "Gestione presenze".
   *
   * Qui distinguiamo subito se l'utente è part-time guardando i dati già
   * presenti nel payload calendario. In base a quello prepariamo:
   * - il form smart standard
   * - il form aggiuntivo di redistribuzione oraria settimanale
   */
  ngOnInit() {
    this.request = this.modalData.request;
    this.user = this.modalData.user;
    this.date = this.modalData.date;
    this.edit = this.modalData.edit;
    this.isPartTimeUser = this.hasPartTimeScheduleInCurrentCalendarWeek();

    this.currentWeekStart = startOfWeek(new Date(this.date), {
      weekStartsOn: 1
    });
    this._initForm();
    this._initScheduleForm();
    this.updateWeekDays();
  }

  /**
   * Espone il FormArray della redistribuzione settimanale part-time.
   *
   * Serve per rendere il template più leggibile quando iteriamo le 5 righe
   * della settimana.
   */
  get weeklyScheduleArray(): FormArray {
    return this.scheduleRequestForm.get('weekly_schedule') as FormArray;
  }

  /**
   * Restituisce i FormGroup delle singole righe orarie nel formato già pronto
   * per il template Angular.
   */
  get weeklyScheduleControls(): FormGroup[] {
    return this.weeklyScheduleArray.controls as FormGroup[];
  }

  /**
   * funzione che genera i giorni della settimana
   */
  generateWeek() {
    this._initForm();
  }

  /**
   * Carica e costruisce il form del tab "Smart working" per la settimana
   * attualmente selezionata.
   *
   * Questo ramo usa il flusso storico già esistente:
   * - legge i tipi di presenza/smart della settimana
   * - salva i valori iniziali
   * - abilita il submit solo se la settimana è stata davvero modificata
   */
  async _initForm() {
    this.isLoadingWeek = true;
    // vado ad effettuare la get per prendermi i dati per popolare la modale
    const res = await firstValueFrom(
      this.rs.getWeek({
        start_date: this.formatDateISO(this.currentWeekStart),
        end_date: this.formatDateISO(addDays(this.currentWeekStart, 4))
      })
    );
    this.maxSmartDays = res.smart_days.max_smart_days;
    this.userWeek = res.result;
    this.copyUserWeek = this.userWeek.map(item => ({ ...item }));
    const startDate = this.currentWeekStart;

    const weekDays = Array.from({ length: this.userWeek.length }).map(
      (_, i) => {
        const date = addDays(startDate, i);
        const label = `${format(date, 'd', { locale: it })} ${format(date, 'EEEE', { locale: it })}`;
        return { date, label };
      }
    );
    this.weekDays = weekDays;

    const group: { [key: string]: FormControl } = {};
    weekDays.forEach((_, i) => {
      //andiamo a popolare il form con i valori ricevuti dall'API
      group[`day${i}`] = new FormControl(
        this.userWeek[i].events[0]?.type ?? 14
      );
    });

    // Salva una copia dei valori iniziali del gruppo week
    this.initialWeekValues = this.weekDays.reduce(
      (acc, _, i) => {
        acc[`day${i}`] = this.userWeek[i].events[0]?.type ?? 14; // o il valore iniziale reale
        return acc;
      },
      {} as { [key: string]: number }
    );

    this.workPlaceRequestForm = this.formBuilder.group({
      week: this.formBuilder.group(group, {
        validators: weekChangedValidator(this.initialWeekValues)
      }),
      note: new FormControl(null, Validators.required)
    });

    this.workPlaceRequestForm.get('week')?.updateValueAndValidity();

    // Ascolta i cambiamenti nel form e confronta
    this.workPlaceRequestForm
      .get('week')!
      .valueChanges.subscribe(currentValues => {
        const isSame = this.areWeekValuesEqual(
          currentValues,
          this.initialWeekValues
        );
        this.weekNavigationDisabled = isSame;

        //Calcolo i giorni di smart dell'utente
        this.totalSmartDays = this.countSmartDays(currentValues);
      });

    const initialValues = this.workPlaceRequestForm.get('week')!.value;
    this.totalSmartDays = this.countSmartDays(initialValues);
    this.isLoadingWeek = false;
  }

  /**
   * Inizializza il form dedicato alla redistribuzione settimanale part-time.
   *
   * Per i full-time resta un form "vuoto" che non viene usato. Per i
   * part-time carichiamo subito la settimana effettiva dal nuovo endpoint BE.
   */
  private _initScheduleForm() {
    this.scheduleRequestForm = this.formBuilder.group({
      note: new FormControl(null, Validators.required),
      weekly_schedule: this.formBuilder.array([])
    });

    this.replaceWeeklySchedule(this.buildEmptyWeeklySchedule());

    if (this.isPartTimeUser) {
      this.loadPartTimeWeek();
    }
  }

  /**
   * Calcola il totale dei giorni smart scelti nel tab smart:
   * - smart intera giornata = 1
   * - smart mattina/pomeriggio = 0.5
   *
   * Questo valore serve sia per la UI sia per bloccare richieste oltre il
   * massimo previsto dal contratto.
   */
  countSmartDays(values: { [key: string]: number }): number {
    let total = 0;
    Object.values(values).forEach(value => {
      if (value === 2) {
        total += 1; // intera giornata
      } else if (value === 3 || value === 4) {
        total += 0.5; // mezza giornata
      }
    });
    return total;
  }

  /**
   * Confronta i valori correnti del tab smart con quelli iniziali caricati dal
   * backend.
   *
   * Serve a capire se l'utente ha davvero cambiato qualcosa prima di abilitare
   * invio e navigazione coerente della settimana.
   */
  private areWeekValuesEqual(
    current: { [key: string]: number },
    initial: { [key: string]: number }
  ): boolean {
    return Object.keys(initial).every(key => current[key] === initial[key]);
  }

  /**
   * Aggiorna il contatore locale dei presenti quando l'utente cambia il tipo di
   * giornata nel tab smart.
   *
   * Non salva nulla a BE: serve solo a rendere coerente la UI della modale
   * prima dell'invio.
   */
  updatePresentWorkers(index: number) {
    const key = `day${index}`;
    const control = this.workPlaceRequestForm.get(['week', key]);
    if (!control) return;

    const newValue = control.value;
    const oldValue = this.initialWeekValues[key];

    // Caso 1: da "presenza" a "smart intera giornata" → -1
    if (this.isPresentValue(oldValue) && newValue === 2) {
      this.userWeek[index].present_users -= 1;
    }
    // Caso 2: da "smart intera giornata" a "presenza" → +1
    else if (oldValue === 2 && this.isPresentValue(newValue)) {
      this.userWeek[index].present_users += 1;
    }
  }

  /**
   * Dice se un valore rappresenta una giornata che occupa comunque un posto in
   * presenza.
   *
   * Nel flusso attuale:
   * - presenza piena = presenza
   * - smart mattina / smart pomeriggio = presenza per mezza giornata
   */
  isPresentValue(value: number): boolean {
    // presenza (14), smart mattina (3), smart pomeriggio (4)
    return value === 14 || value === 3 || value === 4;
  }

  /**
   * Indica se il giorno della settimana è un day off/festività e quindi non è
   * modificabile dal tab smart.
   */
  isDayOff(index: number): boolean {
    return this.userWeek[index]?.day_type === 1;
  }

  /**
   * Dice se, per la settimana corrente visibile in modale, il giorno indicato
   * è non lavorativo secondo la distribuzione part-time effettiva.
   *
   * Questo controllo serve nel tab smart: un utente part-time non deve poter
   * richiedere smart/presenza su una giornata che in quella settimana è stata
   * definita come non lavorativa.
   */
  isPartTimeNonWorkingDay(index: number): boolean {
    if (!this.isPartTimeUser) {
      return false;
    }

    const scheduleRow = this.weeklyScheduleControls[index];
    if (scheduleRow) {
      return !scheduleRow.get('is_working_day')?.value;
    }

    const calendarDay = this.modalData?.user?.days?.[index]?.part_time_schedule;
    return !!calendarDay?.is_part_time && !calendarDay?.is_working_day;
  }

  /**
   * Dice se la singola select di giornata smart è stata modificata rispetto al
   * valore iniziale.
   */
  isControlChanged(index: number): boolean {
    const key = `day${index}`;
    const currentValue = this.workPlaceRequestForm.get(['week', key])?.value;
    return currentValue !== this.initialWeekValues[key];
  }

  /**
   * Salva la richiesta standard di modifica del luogo di lavoro.
   *
   * Questo è il flusso storico del calendario:
   * - confronta solo i giorni realmente cambiati
   * - costruisce il payload `date_array`
   * - invia la richiesta a `storeEventRequest`
   */
  saveWorkplaceRequest() {
    if (this.workPlaceRequestForm.valid) {
      const raw = this.workPlaceRequestForm.value;
      const weekGroup = this.workPlaceRequestForm.get('week') as FormGroup;

      const dataArray: {
        startDate: string;
        endDate: string;
        smart_type: number;
        start_time: string;
        end_time: string;
      }[] = [];

      // mi prendo il form della settimana, me lo ciclo e controllo cosa è stato "sporcato"
      Object.keys(weekGroup.controls).forEach((key, i) => {
        const control = weekGroup.get(key);
        const initialValue = this.initialWeekValues[key];
        if (control && control.value !== initialValue) {
          const date = this.formatDateISO(this.weekDays[i].date); // tipo "2025-07-11"
          const value = control.value;

          dataArray.push({
            startDate: date,
            endDate: date,
            smart_type: value,
            start_time: this.setTime(value, 'start'),
            end_time: this.setTime(value, 'end')
          });
        }
      });

      // payload finale
      const finalPayload = {
        event_type_id: 2,
        notes: this.workPlaceRequestForm.get('note')?.dirty
          ? raw.note
          : undefined,
        date_array: dataArray
      };
      this.rs.storeRequest(finalPayload).subscribe({
        next: () => {
          // Richiamo l'alert direttamente nell'AlertService
          const text = 'cambio smart';
          this.alertService.showSuccessRequestAlert(text);
          //Chiudo la modale
          this.saved.emit({ success: true, date: this.currentWeekStart });
          this.modalRef.close();
        },
        error: () => {
          this.alertService.showErrorRequestAlert();
        }
      });
    } else {
      this.workPlaceRequestForm.markAllAsTouched();
    }
  }

  /**
   * Salva la richiesta di redistribuzione oraria per utenti part-time.
   *
   * Questo flusso è separato da quello smart:
   * - lavora sempre sulla settimana corrente visibile in modale
   * - conserva il monte minuti del contratto attivo
   * - invia il payload al nuovo endpoint BE dedicato
   */
  savePartTimeWeekChangeRequest() {
    if (
      !this.isPartTimeUser ||
      this.scheduleRequestForm.invalid ||
      this.isWeeklyScheduleInvalid()
    ) {
      this.scheduleRequestForm.markAllAsTouched();
      return;
    }

    const payload = {
      week_start: this.formatDateISO(this.currentWeekStart),
      weekly_minutes: this.partTimeWeeklyMinutes,
      note: this.scheduleRequestForm.get('note')?.value,
      days: this.weeklyScheduleArray.getRawValue().map((row: any) => ({
        weekday: row.weekday,
        start_time: row.is_working_day
          ? moment(row.start_time).format('HH:mm')
          : null,
        end_time: row.is_working_day
          ? moment(row.end_time).format('HH:mm')
          : null,
        working_minutes: row.working_minutes,
        is_working_day: !!row.is_working_day
      }))
    };

    this.rs.storePartTimeWeekChange(payload).subscribe({
      next: () => {
        this.alertService.showSuccessRequestAlert(
          'modifica orario settimanale'
        );
        this.saved.emit({ success: true, date: this.currentWeekStart });
        this.modalRef.close();
      },
      error: () => {
        this.alertService.showErrorRequestAlert();
      }
    });
  }

  /**
   * Traduce il tipo di giornata smart nel relativo orario tecnico usato dal BE.
   *
   * Serve solo per il payload del flusso smart storico: il backend riceve
   * sempre un intervallo orario esplicito anche quando la UI lavora a livello
   * di "tipo giornata".
   */
  setTime(value: number, string: string): string {
    if (string == 'start') {
      switch (value) {
        case 14: // presenza
        case 2: // smart intera giornata
        case 3: // smart mattina
          return '09:00';
        case 4: // smart pomeriggio
          return '14:00';
        default:
          return '';
      }
    } else {
      switch (value) {
        case 14: // presenza
        case 2: // smart intera giornata
        case 4: // smart pomeriggio
          return '18:00';
        case 3: // smart mattina
          return '13:00';
        default:
          return '';
      }
    }
  }

  /**
   * Sposta la settimana mostrata in modale avanti o indietro di 7 giorni.
   *
   * Quando l'utente è part-time ricarichiamo anche la settimana effettiva del
   * tab distribuzione oraria, così entrambe le tab restano sincronizzate.
   */
  changeWeek(direction: number) {
    this.currentWeekStart = addDays(this.currentWeekStart, direction * 7);
    this.generateWeek();
    if (this.isPartTimeUser) {
      this.loadPartTimeWeek();
    }
  }

  /**
   * Restituisce l'etichetta leggibile della settimana mostrata in modale.
   *
   * Il formato è sempre lun-ven e viene riusato da entrambe le tab.
   */
  formatWeek(date: Date): string {
    const start = format(date, 'd', { locale: it });
    const end = format(addDays(date, 4), 'd MMMM yyyy', { locale: it });
    return `${start}–${end}`;
  }

  /**
   * Rigenera le etichette dei 5 giorni visibili a partire dal lunedì corrente.
   *
   * Questo serve solo alla UI: il backend continua a lavorare con date ISO.
   */
  updateWeekDays() {
    this.weekDays = Array.from({ length: 5 }).map((_, i) => {
      const date = addDays(this.currentWeekStart, i);
      const dayNumber = format(date, 'd', { locale: it });
      const weekday = format(date, 'EEEE', { locale: it });
      const label = `${dayNumber} ${weekday.toUpperCase()}`;

      return { date, label };
    });
  }

  /**
   * Chiude la modale senza salvare nulla.
   */
  closeModal() {
    this.modalRef.close();
  }

  /**
   * Cambia tab nella modale part-time.
   *
   * Per i full-time questo metodo è ininfluente perché le tab non vengono
   * mostrate.
   */
  setActiveTab(tab: 'smart' | 'schedule') {
    this.activeTab = tab;
  }

  /**
   * Limita la nota del tab smart a 250 caratteri effettivi.
   */
  limitTextLength(): void {
    const control = this.workPlaceRequestForm.get('note');
    if (control) {
      const text = control.value ?? '';
      if (text.length > 249) {
        control.setValue(text.substring(0, 250), { emitEvent: false });
      }
    }
  }

  /**
   * Limita la nota del tab distribuzione oraria a 250 caratteri effettivi.
   */
  limitScheduleNoteTextLength(): void {
    const control = this.scheduleRequestForm.get('note');
    if (control) {
      const text = control.value ?? '';
      if (text.length > 249) {
        control.setValue(text.substring(0, 250), { emitEvent: false });
      }
    }
  }

  /**
   * Uniforma tutte le date FE nel formato ISO atteso dal backend.
   */
  private formatDateISO(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }

  /**
   * Disabilita la modifica di giorni già passati nel tab smart.
   *
   * La redistribuzione part-time usa invece la settimana corrente caricata dal
   * backend e non passa da questo vincolo.
   */
  isPastDay(dayDate: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // ignora orario
    const compare = new Date(dayDate);
    compare.setHours(0, 0, 0, 0);
    return compare < today;
  }

  /**
   * Capisce se l'utente corrente deve vedere anche il tab distribuzione oraria.
   *
   * La scelta è basata sul payload calendario: se almeno un giorno della
   * settimana porta `part_time_schedule.is_part_time`, la modale si comporta da
   * part-time.
   */
  private hasPartTimeScheduleInCurrentCalendarWeek(): boolean {
    return !!this.modalData?.user?.days?.some(
      (day: any) => day?.part_time_schedule?.is_part_time
    );
  }

  /**
   * Carica dal backend la settimana effettiva part-time per il lunedì
   * selezionato in modale.
   *
   * Questo endpoint può restituire:
   * - la settimana standard del contratto
   * - la settimana già confermata per quella data
   * - oppure una richiesta pending già presente su quella settimana
   *
   * Se la settimana è già pending la mostriamo così com'è e permettiamo
   * comunque di modificarla: il salvataggio aggiornerà la stessa richiesta
   * pending invece di crearne una nuova.
   */
  private async loadPartTimeWeek() {
    this.isLoadingScheduleWeek = true;

    try {
      const response = await firstValueFrom(
        this.rs.getPartTimeWeek(
          this.user.id,
          this.formatDateISO(this.currentWeekStart)
        )
      );

      const result = response?.result;
      const contract = result?.contract;
      const days = result?.days ?? [];
      this.scheduleWeekSource = result?.source ?? '';
      this.hasPendingScheduleRequest = result?.source === 'pending_request';

      this.partTimeWeeklyMinutes = Number(contract?.weekly_minutes || 0);
      this.replaceWeeklySchedule(
        days.map((day: any) => this.mapScheduleRow(day))
      );
      this.scheduleRequestForm.patchValue(
        {
          note: result?.request?.note ?? null
        },
        { emitEvent: false }
      );
      this.initialScheduleValues = JSON.stringify({
        note: this.scheduleRequestForm.get('note')?.value,
        weekly_schedule: this.weeklyScheduleArray.getRawValue()
      });
    } finally {
      this.isLoadingScheduleWeek = false;
    }
  }

  /**
   * Attiva o disattiva un giorno lavorativo nel tab distribuzione oraria.
   *
   * Se il giorno diventa non lavorativo azzeriamo orari e minuti.
   * Se torna lavorativo, ripartiamo da un default breve e poi ricalcoliamo.
   */
  onNonWorkingDayChange(index: number, checked: boolean): void {
    const row = this.weeklyScheduleControls[index];

    if (checked) {
      row.patchValue(
        {
          start_time: null,
          end_time: null,
          break_minutes: 0,
          working_minutes: 0,
          is_working_day: false
        },
        { emitEvent: false }
      );
    } else {
      row.patchValue(
        {
          start_time: this.timeToDate('09:00'),
          end_time: this.timeToDate('13:00'),
          is_working_day: true
        },
        { emitEvent: false }
      );
      this.recalculateRow(row);
    }
  }

  /**
   * Converte i minuti lavorativi della giornata nel formato breve mostrato in
   * UI, ad esempio `4h` o `8h`.
   */
  formatWorkingHours(minutes: number): string {
    return `${minutes / 60}h`;
  }

  /**
   * Totale ore correnti della settimana part-time, usato solo per la UI.
   */
  get totalWeeklyScheduleHours(): number {
    return this.totalWeeklyScheduleMinutes / 60;
  }

  /**
   * Somma tutti i minuti della distribuzione settimanale corrente.
   *
   * Questo è il valore confrontato con il monte minuti contrattuale prima di
   * consentire l'invio della richiesta.
   */
  get totalWeeklyScheduleMinutes(): number {
    return this.weeklyScheduleControls.reduce(
      (total, row) => total + Number(row.get('working_minutes')?.value || 0),
      0
    );
  }

  /**
   * Espone le ore settimanali contrattuali nel formato leggibile mostrato nel
   * messaggio informativo della tab part-time.
   */
  get partTimeWeeklyHours(): number {
    return this.partTimeWeeklyMinutes / 60;
  }

  /**
   * Verifica che la distribuzione settimanale part-time sia coerente.
   *
   * Controlli FE:
   * - la somma dei minuti deve combaciare con il contratto
   * - ogni giorno lavorativo deve avere start/end compilati
   */
  isWeeklyScheduleInvalid(): boolean {
    if (!this.isPartTimeUser) {
      return false;
    }

    return (
      this.totalWeeklyScheduleMinutes !== this.partTimeWeeklyMinutes ||
      this.weeklyScheduleControls.some(row => {
        const isWorkingDay = !!row.get('is_working_day')?.value;

        if (!isWorkingDay) {
          return false;
        }

        return !row.get('start_time')?.value || !row.get('end_time')?.value;
      })
    );
  }

  /**
   * Dice se la settimana part-time è stata davvero modificata rispetto a quella
   * caricata dal backend.
   *
   * Serve per evitare richieste inutili "uguali all'originale".
   */
  hasScheduleWeekChanged(): boolean {
    return (
      JSON.stringify({
        note: this.scheduleRequestForm.get('note')?.value,
        weekly_schedule: this.weeklyScheduleArray.getRawValue()
      }) !== this.initialScheduleValues
    );
  }

  /**
   * Sostituisce completamente il FormArray della distribuzione oraria.
   *
   * Ogni riga viene ricreata da zero e agganciata al ricalcolo automatico dei
   * minuti lavorativi.
   */
  private replaceWeeklySchedule(schedule: any[]): void {
    this.weeklyScheduleArray.clear();

    schedule.forEach(day => {
      const group = this.formBuilder.group({
        weekday: [day.weekday],
        label: [day.label],
        start_time: [day.start_time],
        end_time: [day.end_time],
        break_minutes: [day.break_minutes],
        working_minutes: [day.working_minutes],
        is_working_day: [day.is_working_day]
      });

      group.valueChanges.subscribe(() => this.recalculateRow(group));
      this.weeklyScheduleArray.push(group);
      this.recalculateRow(group);
    });
  }

  /**
   * Ricalcola i minuti lavorativi di una singola riga partendo dagli orari
   * scelti.
   *
   * Regola attuale:
   * - se l'intervallo supera 5 ore e attraversa 13:00-14:00, sottraiamo 60 min
   */
  private recalculateRow(row: FormGroup): void {
    const isWorkingDay = !!row.get('is_working_day')?.value;
    const startTime = row.get('start_time')?.value;
    const endTime = row.get('end_time')?.value;

    if (!isWorkingDay || !startTime || !endTime) {
      row.patchValue(
        {
          break_minutes: 0,
          working_minutes: 0
        },
        { emitEvent: false }
      );
      return;
    }

    const start = moment(startTime);
    const end = moment(endTime);

    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      row.patchValue(
        {
          break_minutes: 0,
          working_minutes: 0
        },
        { emitEvent: false }
      );
      return;
    }

    const totalMinutes = end.diff(start, 'minutes');
    const breakMinutes =
      totalMinutes > 300 && this.crossesLunchBreak(start, end) ? 60 : 0;
    const workingMinutes = Math.max(totalMinutes - breakMinutes, 0);

    row.patchValue(
      {
        break_minutes: breakMinutes,
        working_minutes: workingMinutes
      },
      { emitEvent: false }
    );
  }

  /**
   * Mappa una riga proveniente dal backend nel formato usato dal form FE.
   *
   * Qui convertiamo gli orari stringa `HH:mm` in oggetti `Date`, perché i time
   * picker ng-zorro lavorano con quel formato.
   */
  private mapScheduleRow(row: any): any {
    return {
      weekday: row.weekday,
      label:
        this.weeklyDays.find(day => day.weekday === row.weekday)?.label || '',
      start_time: row.start_time ? this.timeToDate(row.start_time) : null,
      end_time: row.end_time ? this.timeToDate(row.end_time) : null,
      break_minutes: Number(row.break_minutes || 0),
      working_minutes: Number(row.working_minutes || 0),
      is_working_day: !!row.is_working_day
    };
  }

  /**
   * Costruisce una settimana vuota usata come placeholder iniziale del tab
   * distribuzione oraria prima del caricamento reale da backend.
   */
  private buildEmptyWeeklySchedule(): any[] {
    return this.weeklyDays.map(day => ({
      weekday: day.weekday,
      label: day.label,
      start_time: null,
      end_time: null,
      break_minutes: 0,
      working_minutes: 0,
      is_working_day: false
    }));
  }

  /**
   * Dice se l'intervallo selezionato attraversa la pausa pranzo standard
   * 13:00-14:00.
   */
  private crossesLunchBreak(start: moment.Moment, end: moment.Moment): boolean {
    const lunchStart = start.clone().hour(13).minute(0).second(0);
    const lunchEnd = start.clone().hour(14).minute(0).second(0);

    return start.isBefore(lunchStart) && end.isAfter(lunchEnd);
  }

  /**
   * Converte una stringa `HH:mm` in `Date` per alimentare i time picker
   * ng-zorro nel form FE.
   */
  private timeToDate(time: string): Date {
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }
}
